import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Link } from 'react-router-dom';

import { StateLanguage } from '../../languages/config/StateLanguage';
import { CopilotMessage } from '../../models/copilot.model';
import CopilotActionCard from './CopilotActionCard';

interface Props {
  msg: CopilotMessage;
}

const routeMap: Record<string, string> = {
  'create_contact': '/main/contact/view/',
  'create_quote': '/main/sale/quote/view/',
  'create_order': '/main/sale/order/view/',
  'create_invoice': '/main/sale/invoice/view/',
  'create_provider_order': '/main/purchase/order/view/',
  'create_provider_invoice': '/main/purchase/invoice/view/',
  'create_expense': '/main/purchase/expense/view/',
  'create_accounting_entry': '/main/accounting/entry/view/',
}

function formatTime(iso: string): string {
  if (!iso) return ''
  try {
    const d = new Date(iso)
    return d.toLocaleTimeString([], {hour: '2-digit', minute: '2-digit'})
  } catch {
    return ''
  }
}

const CopilotBubble: React.FC<Props> = ({msg}) => {
  const {lang} = StateLanguage()

  // Batch milestone messages
  if (msg.role === 'system' && msg.actionToolName === 'batch_update') {
    return (
      <div className="d-flex justify-content-center mb-10">
        <div className="p-3 rounded bg-light-warning text-gray-800 mw-lg-400px text-center" style={{wordBreak: 'break-word'}}>
          <span className="fs-8 fw-bold">{lang.labels.copilotBatchMilestone}</span>
          {msg.content && <div className="fs-8 mt-1">{msg.content}</div>}
        </div>
      </div>
    )
  }

  // System action result messages
  if (msg.role === 'system' && msg.actionToolName !== undefined) {
    const successLabels: Record<string, string> = {
      'create_contact': lang.labels.copilotResultContactCreated,
      'create_invoice': lang.labels.copilotResultInvoiceCreated,
      'create_quote': lang.labels.copilotResultQuoteCreated,
      'create_order': lang.labels.copilotResultOrderCreated,
      'create_provider_invoice': lang.labels.copilotResultProviderInvoiceCreated,
      'create_provider_order': lang.labels.copilotResultProviderOrderCreated,
      'create_expense': lang.labels.copilotResultExpenseCreated,
      'create_accounting_entry': lang.labels.copilotResultAccountingEntryCreated,
    }

    // Rich read-only card for history messages with preview data
    if (msg.preview && msg.actionToolName) {
      const errorLabel = msg.actionError === '__rejected__' ? lang.labels.copilotActionRejected
        : msg.actionError === '__failed__' ? lang.labels.copilotActionFailedPast
        : undefined
      return (
        <div className="d-flex justify-content-start mb-10">
          <div className="d-flex flex-column align-items-start" style={{width: '100%'}}>
            {errorLabel && (
              <div className="mb-2">
                <span className="badge badge-light-danger fs-8">{'\u274C'} {errorLabel}</span>
              </div>
            )}
            <CopilotActionCard
              action={{ actionId: msg.id, toolName: msg.actionToolName, preview: msg.preview, status: 'pending' }}
              onConfirm={() => {}}
              onReject={() => {}}
              readOnly
              resultId={msg.actionResultId}
              resultSuccess={msg.actionSuccess}
            />
          </div>
        </div>
      )
    }

    let text: string
    let viewUrl: string | null = null

    if (msg.actionSuccess) {
      text = `\u2705 ${successLabels[msg.actionToolName] || lang.labels.copilotResultGeneric}`
      if (msg.actionResultId) {
        const base = routeMap[msg.actionToolName]
        if (base) viewUrl = base + msg.actionResultId
      }
    } else {
      const errorLabel = msg.actionError === '__rejected__' ? lang.labels.copilotActionRejected
        : msg.actionError === '__failed__' ? lang.labels.copilotActionFailedPast
        : (msg.actionError || lang.labels.copilotResultFailed)
      text = `\u274C ${errorLabel}`
    }

    return (
      <div className="d-flex justify-content-center mb-10">
        <div className={`p-4 rounded ${msg.actionSuccess ? 'bg-light-success' : 'bg-light-danger'} text-gray-800 mw-lg-400px`} style={{wordBreak: 'break-word'}}>
          <span className="fs-7 fw-bold">{text}</span>
          {viewUrl && (
            <div className="mt-1">
              <Link to={viewUrl} target="_blank" rel="noopener noreferrer" className="fs-8 fw-bold text-primary text-hover-dark">
                {lang.labels.copilotViewPiece} &rarr;
              </Link>
            </div>
          )}
        </div>
      </div>
    )
  }

  // System messages (non-action)
  if (msg.role === 'system') {
    return (
      <div className="d-flex justify-content-center mb-10">
        <div className="p-3 rounded bg-light text-muted fs-8 mw-lg-400px text-center" style={{wordBreak: 'break-word'}}>
          {msg.content}
        </div>
      </div>
    )
  }

  const isUser = msg.role === 'user'
  const time = formatTime(msg.createdAt)

  // User bubble (outgoing - right aligned)
  if (isUser) {
    return (
      <div className="d-flex justify-content-end mb-10">
        <div className="d-flex flex-column align-items-end">
          <div className="d-flex align-items-center mb-2">
            {time && <span className="text-muted fs-8 me-3">{time}</span>}
            <span className="fs-7 fw-bolder text-gray-900">{lang.labels.copilotYou}</span>
            <div className="symbol symbol-35px ms-3">
              <div className="symbol-label fs-8 fw-bold bg-light-primary text-primary rounded-circle">
                <span className="svg-icon svg-icon-3 svg-icon-primary">
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <path d="M6.28548 15.0861C7.34369 13.1814 9.35142 12 11.5304 12H12.4696C14.6486 12 16.6563 13.1814 17.7145 15.0861L19.3493 18.0287C20.0899 19.3618 19.1259 21 17.601 21H6.39903C4.87406 21 3.91012 19.3618 4.65071 18.0287L6.28548 15.0861Z" fill="currentColor"/>
                    <rect opacity="0.3" x="8" y="3" width="8" height="8" rx="4" fill="currentColor"/>
                  </svg>
                </span>
              </div>
            </div>
          </div>
          <div className="p-5 rounded bg-light-primary text-dark mw-lg-400px text-end" style={{wordBreak: 'break-word'}}>
            <span className="fs-7" style={{whiteSpace: 'pre-wrap'}}>{msg.content}</span>
          </div>
        </div>
      </div>
    )
  }

  // Assistant bubble (incoming - left aligned)
  return (
    <div className="d-flex justify-content-start mb-10">
      <div className="d-flex flex-column align-items-start">
        <div className="d-flex align-items-center mb-2">
          <div className="symbol symbol-35px me-3">
            <div className="symbol-label fs-8 fw-bold bg-light-info text-info rounded-circle">
              <span className="svg-icon svg-icon-3 svg-icon-info">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path opacity="0.3" d="M20 3H4C2.89543 3 2 3.89543 2 5V16C2 17.1046 2.89543 18 4 18H4.5C5.05228 18 5.5 18.4477 5.5 19V21.5052C5.5 22.1441 6.21232 22.5253 6.74118 22.1708L11.3753 19.0955C11.5301 18.9912 11.7123 18.9354 11.8989 18.9354H20C21.1046 18.9354 22 18.0399 22 16.9354V5C22 3.89543 21.1046 3 20 3Z" fill="currentColor"/>
                  <rect x="6" y="12" width="7" height="2" rx="1" fill="currentColor"/>
                  <rect x="6" y="7" width="12" height="2" rx="1" fill="currentColor"/>
                </svg>
              </span>
            </div>
          </div>
          <span className="fs-7 fw-bolder text-gray-900 me-3">{lang.labels.copilotTitle}</span>
          {time && <span className="text-muted fs-8">{time}</span>}
        </div>
        <div className="p-5 rounded bg-light-info text-dark mw-lg-400px text-start" style={{wordBreak: 'break-word'}}>
          <div className="copilot-markdown fs-7">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{msg.content}</ReactMarkdown>
          </div>
        </div>
      </div>
    </div>
  )
}

export default CopilotBubble;
